import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { beehiveTheme } from '../../styles/beehiveTheme';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(15, 0),
  backgroundColor: 'white',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '2.75rem',
  marginBottom: theme.spacing(4),
  color: beehiveTheme.primary.main,
  lineHeight: 1.2,
  [theme.breakpoints.down('md')]: {
    fontSize: '2.25rem',
  },
}));

const Paragraph = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  color: '#4a5568',
  lineHeight: 1.9,
  marginBottom: theme.spacing(4),
}));

const FeatureCard = styled(Box)(({ theme }) => ({
  padding: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const IconCircle = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: beehiveTheme.gradients.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  color: beehiveTheme.accent.main,
}));

const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: beehiveTheme.primary.main,
  marginBottom: theme.spacing(2),
}));

const FeatureText = styled(Typography)(({ theme }) => ({
  fontSize: '1.05rem',
  color: '#4a5568',
  lineHeight: 1.8,
}));

const BigCallout = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 6),
  backgroundColor: beehiveTheme.primary.main,
  borderRadius: '12px',
  marginTop: theme.spacing(10),
  marginBottom: theme.spacing(10),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '300px',
    height: '300px',
    background: `radial-gradient(circle, ${beehiveTheme.accent.main}15 0%, transparent 70%)`,
    transform: 'translate(30%, -30%)',
  },
}));

const TechDetail = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  paddingLeft: theme.spacing(2),
  borderLeft: `3px solid ${beehiveTheme.accent.main}`,
}));

export default function SolutionSection() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const features = [
    {
      icon: <CameraAltIcon sx={{ fontSize: 40 }} />,
      title: 'Computer Vision',
      text: 'Dual high-resolution cameras capture bees at hive entrances. AI analyzes each frame to detect mites as small as 1.1mm—even on moving bees.',
    },
    {
      icon: <GraphicEqIcon sx={{ fontSize: 40 }} />,
      title: 'Acoustic Analysis',
      text: 'Microphones continuously monitor hive sounds. Stressed colonies have distinct acoustic signatures that our models can identify.',
    },
    {
      icon: <ThermostatIcon sx={{ fontSize: 40 }} />,
      title: 'Environmental Sensing',
      text: 'Track temperature, humidity, CO₂, and VOC levels in real-time. Environmental changes often signal problems before visual symptoms appear.',
    },
    {
      icon: <PsychologyIcon sx={{ fontSize: 40 }} />,
      title: 'Predictive AI',
      text: "Machine learning models don't just detect problems—they forecast infestation trajectories weeks ahead, giving you time to act.",
    },
  ];

  return (
    <SectionContainer id="solution">
      <Container maxWidth="lg">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeIn}>
          <Box sx={{ maxWidth: '800px', margin: '0 auto', mb: 10 }}>
            <Title>
              We're building a system that watches your hives 24/7 and catches problems weeks before they become critical.
            </Title>

            <Paragraph>
              Instead of checking manually every few weeks, imagine continuous monitoring that actually understands 
              what's happening inside your hives.
            </Paragraph>
          </Box>
        </motion.div>

        {/* Feature Grid */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
              >
                <FeatureCard>
                  <IconCircle>
                    {feature.icon}
                  </IconCircle>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureText>{feature.text}</FeatureText>
                </FeatureCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Big Callout */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeIn}>
          <BigCallout>
            <Typography 
              sx={{ 
                fontSize: '2rem', 
                fontWeight: 700, 
                color: 'white', 
                mb: 3, 
                lineHeight: 1.3,
                position: 'relative',
                zIndex: 1,
              }}
            >
              The difference? We catch infestations when they're manageable, not when they're catastrophic.
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '1.2rem', 
                color: '#e2e8f0', 
                lineHeight: 1.8,
                position: 'relative',
                zIndex: 1,
              }}
            >
              Traditional methods tell you what happened last week. Our system tells you what's likely to happen 
              next week—giving you the time you need to protect your colonies.
            </Typography>
          </BigCallout>
        </motion.div>

        {/* Technical Details */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeIn}>
          <Box sx={{ maxWidth: '800px', margin: '0 auto' }}>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: beehiveTheme.primary.main, mb: 4 }}>
              How it works in practice:
            </Typography>

            <TechDetail>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: beehiveTheme.accent.main, flexShrink: 0 }}>
                01
              </Typography>
              <Box>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: beehiveTheme.primary.main, mb: 1 }}>
                  Everything runs locally
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', color: '#4a5568', lineHeight: 1.8 }}>
                  Edge computing devices (Raspberry Pi or NVIDIA Jetson) process data right at the hive. Fast, 
                  private, and no cloud subscriptions needed.
                </Typography>
              </Box>
            </TechDetail>

            <TechDetail>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: beehiveTheme.accent.main, flexShrink: 0 }}>
                02
              </Typography>
              <Box>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: beehiveTheme.primary.main, mb: 1 }}>
                  Multiple sensors, one decision
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', color: '#4a5568', lineHeight: 1.8 }}>
                  By combining visual, acoustic, and environmental data, the system stays reliable even when individual 
                  sensors face challenges like poor lighting or high background noise.
                </Typography>
              </Box>
            </TechDetail>

            <TechDetail>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: beehiveTheme.accent.main, flexShrink: 0 }}>
                03
              </Typography>
              <Box>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: beehiveTheme.primary.main, mb: 1 }}>
                  Remote monitoring included
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', color: '#4a5568', lineHeight: 1.8 }}>
                  LoRa connectivity lets you monitor distant apiaries without WiFi or cellular coverage. Get alerts 
                  on your phone no matter where your bees are.
                </Typography>
              </Box>
            </TechDetail>

            <TechDetail>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: beehiveTheme.accent.main, flexShrink: 0 }}>
                04
              </Typography>
              <Box>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: beehiveTheme.primary.main, mb: 1 }}>
                  No bees harmed
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', color: '#4a5568', lineHeight: 1.8 }}>
                  Unlike alcohol wash tests that sacrifice hundreds of bees, our system is completely non-invasive. 
                  Monitor as frequently as you want without impacting colony health.
                </Typography>
              </Box>
            </TechDetail>
          </Box>
        </motion.div>
      </Container>
    </SectionContainer>
  );
}
